import {
  lendingApplicationMessages,
  lendingOnboardingSteps,
  applicationDefaults,
  storageKeys,
  income_frequencies,
  errorMessages,
} from 'utils';
import { request } from './request';
import { Cache } from './cache.service';

function save() {
  return {
    async searchCustomer(application, property) {
      const resiliency = {
        authRetries: 1,
        response: null,
      };

      const value = application.personal[property];
      const fetchOptions = { method: 'GET' };

      resiliency.response = await request(`/customers/property/${property}?value=${value}`, {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: false,
        ignoreErrorCodes: [401],
      });

      return resiliency.response;
    },

    async createCustomer(application) {
      const resiliency = {
        authRetries: 1,
        response: null,
      };

      const personal = application.personal;
      const fetchOptions = {
        method: 'POST',
        body: JSON.stringify({
          email: personal.email,
          idNumber: personal.idNumber,
          clientReference: personal.clientReference,
          firstName: personal.firstName,
          lastName: personal.lastName,
        }),
      };

      resiliency.response = await request('/customers', {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: true,
        ignoreErrorCodes: [401],
      });

      return resiliency.response;
    },

    async getQuotes(application) {
      const resiliency = {
        authRetries: 1,
        response: null,
      };
      const personalDetails = application.personal;
      const requestDetails = application.request;
      const bank = application?.bank;
      const gross_income = parseFloat(requestDetails.grossIncome || 0);
      const expenses = parseFloat(requestDetails.estimatedExpenses || 0);

      const fetchOptions = {
        method: 'POST',
        body: JSON.stringify({
          affiliate_id: '191',
          id_number: personalDetails.idNumber,
          first_name: personalDetails.firstName,
          middle_name: '',
          last_name: personalDetails.lastName,
          cell_phone_number: `0${personalDetails.mobileNumber.substring(
            3,
            personalDetails.mobileNumber.length,
          )}`,
          email: personalDetails.email,
          gross_income: gross_income,
          expenses: expenses,
          net_income: gross_income - expenses,
          payment_frequency: requestDetails.incomeFrequency || income_frequencies.weekly,
          payday: 0,
          bank_name: bank?.bankName || applicationDefaults.banks.nedbank.value,
          bank: bank?.bankName || applicationDefaults.banks.nedbank.value,
          type: null,
          popi: true,
          initial_popi: false,
          intent_id: 2,
          loan_amount_required: parseInt(requestDetails.loanAmount),
          repayment_period: parseInt(requestDetails.loanTerm),
          application_method: requestDetails.applicationMethod,
          fast_application: true,
          employed: false,
          income_source: requestDetails.incomeSource,
        }),
      };

      resiliency.response = await request('/v1/offer', {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: true,
        ignoreErrorCodes: [401],
      });

      return resiliency.response;
    },

    async acceptOffer(application) {
      const resiliency = {
        authRetries: 1,
        response: null,
      };

      const { hashId, partnerId, liveScore } = application.selectedOffer;

      const reqBody = {
        hashid: hashId,
        partnerId: partnerId,
        type: application.request.applicationMethod,
      };

      if (applicationDefaults.applicationMethods.apply === application.request.applicationMethod) {
        reqBody.liveScore = liveScore;
      }

      const fetchOptions = {
        method: 'POST',
        body: JSON.stringify(reqBody),
      };

      resiliency.response = await request('/v1/accept', {
        fetchOptions,
        baseURLOverwrite: null,
        tealiumErrorLogger: null,
        suppressTealiumEventOnError: false,
        showToastOnError: true,
        ignoreErrorCodes: [401],
      });

      return resiliency.response;
    },
  };
}

export const compareBackend = () => ({
  async fetchQuotes(application) {
    const client = save();
    return client.getQuotes(application);
  },

  async saveApplication(application) {
    if (application.canSave) {
      const client = save();
      const errorOutcome = {
        successful: false,
        techError: false,
        message: errorMessages.technical.general,
      };

      try {
        switch (application.lastStep) {
          case lendingOnboardingSteps.personal_details: {
            const emailResponse = await client.searchCustomer(application, 'email');
            const idNumberResponse = await client.searchCustomer(application, 'idNumber');

            if (emailResponse.status >= 500 || idNumberResponse.status >= 500) {
              return errorOutcome;
            }
            if (emailResponse.status === 200 || idNumberResponse.status === 200) {
              const customer =
                emailResponse.status === 200 ? emailResponse.result : idNumberResponse.result;
              application.personal.clientId = customer.id;
              Cache.save(storageKeys.compare_application, application);
              break;
            }

            const customerResponse = await client.createCustomer(application);
            if (customerResponse.status >= 400) {
              return errorOutcome;
            }
            application.personal.clientId = customerResponse.result?.id;
            Cache.save(storageKeys.compare_application, application);
            break;
          }

          case lendingOnboardingSteps.offers_details: {
            const acceptOfferResponse = await client.acceptOffer(application);
            if (acceptOfferResponse.status >= 400) {
              return errorOutcome;
            }

            const acceptOffer = acceptOfferResponse.result;
            if (acceptOffer && acceptOffer.ok) {
              break;
            }
            break;
          }

          case lendingOnboardingSteps.thank_you: {
            await client.acceptOffer(application);
            break;
          }
          default:
            return { successful: true, message: lendingApplicationMessages.actions.unchanged };
        }
      } catch (error) {
        console.error(error);
        errorOutcome.techError = true;
        errorOutcome.message = error;
        return errorOutcome;
      }
    }

    return { successful: true, message: lendingApplicationMessages.actions.unchanged };
  },
});
